import React, { useEffect, useState } from "react";
import "./dstyle.css";
import SideBar from "./SideBar";
import Navbar from "./Navbar";
import { API_BASE_URL } from "../../config/api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faEdit,
  faTrash,
  faUser,
  faKey,
  faEnvelope,
  faPhone,
  faBriefcase,
  faSpinner
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { Modal } from "antd";
import { toast } from "react-toastify";

function Users() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);

  // Delete Modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Edit Modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    id: null,
    username: "",
    email: "",
    password: "",
    phno: "",
    profession: "",
    gender: "",
    dob: "",
    location: "",
    linkedin_url: "",
    github_url: ""
  });
  const [savingEdit, setSavingEdit] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem("adminId")) { navigate("/admin-login"); return; }
    if (localStorage.getItem("adminRole") !== "main") { navigate("/dashboard"); return; }
    fetch(`${API_BASE_URL}/api/users`)
      .then((res) => res.json())
      .then((data) => {
        setUsers(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching users:", err);
        setLoading(false);
      });
  }, [refreshKey]);

  // Open Edit Modal
  const handleOpenEdit = (user) => {
    setEditForm({
      id: user.id,
      username: user.username || "",
      email: user.email || "",
      password: "", // blank unless changed
      phno: user.phno || "",
      profession: user.profession || "",
      gender: user.gender || "",
      dob: user.dob || "",
      location: user.location || "",
      linkedin_url: user.linkedin_url || "",
      github_url: user.github_url || ""
    });
    setEditModalOpen(true);
  };

  // Submit Edit User (Update Username, Email, Password, Profession, Phone)
  const handleSaveEdit = async () => {
    if (!editForm.username || !editForm.email) {
      toast.error("Username and Email are required.");
      return;
    }
    setSavingEdit(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/users/${editForm.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(editForm)
      });
      if (response.ok) {
        toast.success("User details updated successfully!");
        setEditModalOpen(false);
        setRefreshKey((prev) => prev + 1);
      } else {
        toast.error("Failed to update user details.");
      }
    } catch (err) {
      console.error("Error updating user:", err);
      toast.error("Error connecting to server.");
    } finally {
      setSavingEdit(false);
    }
  };

  // Delete User
  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/users/${selectedUser.id}`, {
        method: "DELETE"
      });
      if (response.ok || response.status === 204) {
        toast.success(`User "${selectedUser.username}" deleted.`);
        setDeleteModalOpen(false);
        setSelectedUser(null);
        setRefreshKey((prev) => prev + 1);
      } else {
        toast.error("Could not delete user.");
      }
    } catch (err) {
      console.error("Error deleting user:", err);
      toast.error("Error connecting to server.");
    }
  };

  // Filtered Users list
  const filteredUsers = users.filter((u) => {
    const q = searchTerm.toLowerCase();
    return (
      (u.username || "").toLowerCase().includes(q) ||
      (u.email || "").toLowerCase().includes(q) ||
      (u.profession || "").toLowerCase().includes(q) ||
      (u.phno || "").toLowerCase().includes(q)
    );
  });

  return (
    <div style={{ backgroundColor: "#f8fafc", minHeight: "100vh" }}>
      <SideBar current={"user"} />
      <section id="content">
        <Navbar />
        <main style={{ padding: "28px" }}>
          {/* Header */}
          <div style={{ marginBottom: "24px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "16px" }}>
            <div>
              <h1 style={{ color: "#0f172a", fontFamily: "'Outfit', sans-serif", fontSize: "1.8rem", fontWeight: 800, margin: 0 }}>
                User Management
              </h1>
              <p style={{ color: "#64748b", margin: "4px 0 0", fontSize: "0.9rem" }}>
                View registered students, update email/password credentials, or remove accounts
              </p>
            </div>

            {/* Search Input */}
            <div style={{ position: "relative", minWidth: "280px" }}>
              <FontAwesomeIcon icon={faSearch} style={{ position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }} />
              <input
                type="text"
                placeholder="Search user by name, email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px 14px 10px 40px",
                  borderRadius: "10px",
                  border: "1.5px solid #cbd5e1",
                  fontSize: "0.88rem",
                  outline: "none",
                  background: "#fff"
                }}
              />
            </div>
          </div>

          {/* Table Container */}
          <div
            style={{
              background: "#fff",
              borderRadius: "20px",
              border: "1px solid #e2e8f0",
              boxShadow: "0 4px 6px -1px rgba(0,0,0,0.03)",
              overflow: "hidden"
            }}
          >
            {loading ? (
              <div style={{ textAlign: "center", padding: "50px 0", color: "#64748b" }}>
                <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: "2rem", color: "#4f46e5", marginBottom: "12px" }} />
                <p>Loading user list...</p>
              </div>
            ) : filteredUsers.length === 0 ? (
              <p style={{ padding: "40px", textAlign: "center", color: "#94a3b8" }}>
                No users found matching your search.
              </p>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left", fontSize: "0.9rem" }}>
                  <thead>
                    <tr style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0", color: "#64748b", fontWeight: "700" }}>
                      <th style={{ padding: "14px 24px" }}>ID</th>
                      <th style={{ padding: "14px 24px" }}>User Info</th>
                      <th style={{ padding: "14px 24px" }}>Email</th>
                      <th style={{ padding: "14px 24px" }}>Phone</th>
                      <th style={{ padding: "14px 24px" }}>Profession</th>
                      <th style={{ padding: "14px 24px", textAlign: "center" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((user) => (
                      <tr key={user.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                        <td style={{ padding: "16px 24px", color: "#94a3b8", fontWeight: "600" }}>
                          #{user.id}
                        </td>
                        <td style={{ padding: "16px 24px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                            <div
                              style={{
                                width: "36px",
                                height: "36px",
                                borderRadius: "50%",
                                background: "#e0e7ff",
                                color: "#4f46e5",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontWeight: "800",
                                fontSize: "0.9rem"
                              }}
                            >
                              {(user.username || "U").charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <div style={{ fontWeight: "700", color: "#0f172a" }}>{user.username}</div>
                              <div style={{ fontSize: "0.78rem", color: "#94a3b8" }}>{user.location || "Global"}</div>
                            </div>
                          </div>
                        </td>
                        <td style={{ padding: "16px 24px", color: "#334155" }}>
                          <FontAwesomeIcon icon={faEnvelope} style={{ marginRight: "6px", color: "#818cf8" }} />
                          {user.email}
                        </td>
                        <td style={{ padding: "16px 24px", color: "#334155" }}>
                          {user.phno || "N/A"}
                        </td>
                        <td style={{ padding: "16px 24px", color: "#334155" }}>
                          <span style={{ background: "#f1f5f9", padding: "4px 12px", borderRadius: "20px", fontSize: "0.82rem", fontWeight: "600" }}>
                            {user.profession || "Student"}
                          </span>
                        </td>
                        <td style={{ padding: "16px 24px", textAlign: "center" }}>
                          <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
                            {/* Edit User Button */}
                            <button
                              onClick={() => handleOpenEdit(user)}
                              style={{
                                background: "#e0e7ff",
                                color: "#4f46e5",
                                border: "none",
                                borderRadius: "8px",
                                padding: "8px 12px",
                                cursor: "pointer",
                                fontSize: "0.85rem",
                                fontWeight: "600"
                              }}
                              title="Edit User Info or Password"
                            >
                              <FontAwesomeIcon icon={faEdit} style={{ marginRight: "4px" }} /> Edit
                            </button>

                            {/* Delete User Button */}
                            <button
                              onClick={() => {
                                setSelectedUser(user);
                                setDeleteModalOpen(true);
                              }}
                              style={{
                                background: "#fee2e2",
                                color: "#ef4444",
                                border: "none",
                                borderRadius: "8px",
                                padding: "8px 12px",
                                cursor: "pointer",
                                fontSize: "0.85rem",
                                fontWeight: "600"
                              }}
                              title="Delete User Account"
                            >
                              <FontAwesomeIcon icon={faTrash} /> Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </main>
      </section>

      {/* Edit User Modal */}
      <Modal
        title={`Edit Account: ${editForm.username}`}
        open={editModalOpen}
        onOk={handleSaveEdit}
        onCancel={() => setEditModalOpen(false)}
        confirmLoading={savingEdit}
        okText="Save Changes"
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "14px", paddingTop: "14px" }}>
          <div>
            <label style={{ fontSize: "0.82rem", fontWeight: 700, color: "#334155", marginBottom: "4px", display: "block" }}>
              Username
            </label>
            <input
              type="text"
              value={editForm.username}
              onChange={(e) => setEditForm({ ...editForm, username: e.target.value })}
              style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", border: "1px solid #cbd5e1" }}
            />
          </div>

          <div>
            <label style={{ fontSize: "0.82rem", fontWeight: 700, color: "#334155", marginBottom: "4px", display: "block" }}>
              Email Address
            </label>
            <input
              type="email"
              value={editForm.email}
              onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
              style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", border: "1px solid #cbd5e1" }}
            />
          </div>

          <div>
            <label style={{ fontSize: "0.82rem", fontWeight: 700, color: "#334155", marginBottom: "4px", display: "block" }}>
              New Password (leave blank to keep current)
            </label>
            <input
              type="password"
              placeholder="Enter new password to change..."
              value={editForm.password}
              onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
              style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", border: "1px solid #cbd5e1" }}
            />
          </div>

          <div>
            <label style={{ fontSize: "0.82rem", fontWeight: 700, color: "#334155", marginBottom: "4px", display: "block" }}>
              Phone Number
            </label>
            <input
              type="text"
              value={editForm.phno}
              onChange={(e) => setEditForm({ ...editForm, phno: e.target.value })}
              style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", border: "1px solid #cbd5e1" }}
            />
          </div>

          <div>
            <label style={{ fontSize: "0.82rem", fontWeight: 700, color: "#334155", marginBottom: "4px", display: "block" }}>
              Profession
            </label>
            <input
              type="text"
              value={editForm.profession}
              onChange={(e) => setEditForm({ ...editForm, profession: e.target.value })}
              style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", border: "1px solid #cbd5e1" }}
            />
          </div>
        </div>
      </Modal>

      {/* Delete User Modal */}
      <Modal
        title="Confirm User Deletion"
        open={deleteModalOpen}
        onOk={handleDeleteUser}
        onCancel={() => setDeleteModalOpen(false)}
        okText="Yes, Delete User"
        okButtonProps={{ danger: true }}
      >
        <p style={{ color: "#334155", fontSize: "0.95rem" }}>
          Are you sure you want to delete user account <strong>"{selectedUser?.username}"</strong> ({selectedUser?.email})?
        </p>
      </Modal>
    </div>
  );
}

export default Users;
